import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Loader from '../common/Loader.js';
import axios from '../../config/axios';
import { useTranslation } from 'react-i18next';
import { downloadBlob } from '../../utils/downloadBlob.js';


const PublicViewReturn = () => {
  const { t } = useTranslation();
  const { returnId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [returnData, setReturnData] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState(null);
  const [downloadingReceipt, setDownloadingReceipt] = useState(false);
  const [loadingReceipt, setLoadingReceipt] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchReturn();
  }, [returnId]);

  const fetchReturn = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get(`/shipments/returned/${returnId}`);
      const returnInfo = response.data.data;

      if (returnInfo) {
        setReturnData(returnInfo);
        if (returnInfo.has_payment_receipt) {
          fetchReceiptPreview();
        }
      } else {
        setError('Return request not found');
      }
    } catch (error) {
      console.error('Error fetching return:', error);
      setError(error.response?.data?.error || 'Failed to load return request');
    } finally {
      setLoading(false);
    }
  };

  const fetchReceiptPreview = async () => {
    try {
      setLoadingReceipt(true);
      const response = await axios.get(`/shipments/returned/${returnId}/receipt`, {
        responseType: 'blob'
      });

      const reader = new FileReader();
      reader.onload = () => {
        const base64String = reader.result.split(',')[1];
        const blob = response.data;
        const mimeType = blob.type || 'application/octet-stream';

        setReceiptPreview({
          receipt_base64: base64String,
          mime_type: mimeType,
          filename: `payment_receipt_${returnId}`,
        });
      };
      reader.readAsDataURL(response.data);
    } catch (error) {
      console.error('Error loading receipt preview:', error);
    } finally {
      setLoadingReceipt(false);
    }
  };

  const handleDownloadReceipt = async () => {
    try {
      setDownloadingReceipt(true);
      const response = await axios.get(`/shipments/returned/${returnId}/receipt`, {
        responseType: 'blob'
      });
      await downloadBlob(
        response.data,
        receiptPreview?.filename || 'payment_receipt.pdf',
        receiptPreview?.mime_type || 'application/pdf',
      );
    } catch (error) {
      console.error('Error downloading receipt:', error);
      alert('Failed to download payment receipt');
    } finally {
      setDownloadingReceipt(false);
    }
  };

  const getStatusBadgeColor = (status) => {
    const colors = {
      open: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      close: 'bg-green-100 text-green-800 border-green-200',
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      approved: 'bg-green-100 text-green-800 border-green-200',
      rejected: 'bg-red-100 text-red-800 border-red-200',
    };
    return colors[status?.toLowerCase()] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'open':
        return '📖';
      case 'close':
        return '✅';
      case 'pending':
        return '⏳';
      case 'approved':
        return '✅';
      case 'rejected':
        return '❌';
      default:
        return '📋';
    }
  };

  const isPaymentInitiated = returnData?.payment_initiated?.approved || false;
  const isPaymentRefunded = returnData?.payment_refunded?.approved || false;

  const ReceiptModal = () => {
    if (!showReceiptModal) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
          <div className="bg-gradient-to-r from-green-500 to-green-600 px-6 py-4 flex items-center justify-between">
            <h3 className="text-lg font-bold text-white">Payment Receipt</h3>
            <button
              onClick={() => setShowReceiptModal(false)}
              className="text-white hover:bg-white/20 p-2 rounded-lg transition"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="flex-1 overflow-auto p-6">
            {loadingReceipt ? (
              <div className="flex items-center justify-center h-96">
                <Loader fullScreen={false} size="small" message="Loading receipt..." />
              </div>
            ) : receiptPreview ? (
              <div className="flex justify-center">
                <img
                  src={`data:${receiptPreview.mime_type};base64,${receiptPreview.receipt_base64}`}
                  alt="Payment Receipt"
                  className="max-w-full h-auto rounded-lg shadow-lg border-2 border-gray-200"
                />
              </div>
            ) : (
              <div className="text-center text-gray-500">No receipt available</div>
            )}
          </div>

          <div className="bg-gray-50 border-t border-gray-200 px-6 py-4 flex gap-3">
            <button
              onClick={() => setShowReceiptModal(false)}
              className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-semibold transition"
            >
              Close
            </button>
            <button
              onClick={handleDownloadReceipt}
              disabled={downloadingReceipt}
              className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center justify-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                />
              </svg>
              {downloadingReceipt ? 'Downloading...' : t('common.download')}
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return <Loader fullScreen={true} size="default" message="Loading return request..." />
  }

  if (error || !returnData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-500 via-orange-600 to-orange-700">
        {/* Navigation */}
        <nav className="bg-white bg-opacity-10 backdrop-blur-md border-b border-white border-opacity-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3 cursor-pointer" onClick={() => navigate('/')}>
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white rounded-lg flex items-center justify-center">
                <span className="text-orange-500 font-bold text-lg sm:text-xl">L</span>
              </div>
              <h1 className="text-lg sm:text-2xl font-bold text-white">Logistics Manager</h1>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              <button
                onClick={() => navigate('/')}
                className="px-3 sm:px-5 py-1.5 sm:py-2 text-sm sm:text-base text-white hover:bg-white hover:bg-opacity-10 rounded-lg font-semibold transition-all"
              >
                Home
              </button>
              <button
                onClick={() => navigate('/login')}
                className="px-3 sm:px-5 py-1.5 sm:py-2 text-sm sm:text-base bg-white text-orange-600 font-semibold rounded-lg hover:bg-gray-100 transition-all shadow-md"
              >
                Login
              </button>
            </div>
          </div>
        </nav>

        <div className="max-w-4xl mx-auto px-4 py-8 flex items-center justify-center min-h-[calc(100vh-80px)]">
          <div className="bg-white rounded-2xl shadow-xl border-2 border-red-200 p-8 max-w-md text-center">
            <div className="text-6xl mb-4">❌</div>
            <p className="text-red-600 text-lg font-bold mb-2">Return Request Not Found</p>
            <p className="text-gray-600 mb-4">{error || 'The return request you are looking for does not exist.'}</p>
            <button
              onClick={() => navigate('/public-track-shipment')}
              className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 font-semibold transition"
            >
              Track Shipment
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-500 via-orange-600 to-orange-700">
      {/* Navigation */}
      <nav className="bg-white bg-opacity-10 backdrop-blur-md border-b border-white border-opacity-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white rounded-lg flex items-center justify-center">
              <span className="text-orange-500 font-bold text-lg sm:text-xl">L</span>
            </div>
            <h1 className="text-lg sm:text-2xl font-bold text-white">Logistics Manager</h1>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <button
              onClick={() => navigate('/')}
              className="px-3 sm:px-5 py-1.5 sm:py-2 text-sm sm:text-base text-white hover:bg-white hover:bg-opacity-10 rounded-lg font-semibold transition-all"
            >
              Home
            </button>
            <button
              onClick={() => navigate('/login')}
              className="px-3 sm:px-5 py-1.5 sm:py-2 text-sm sm:text-base bg-white text-orange-600 font-semibold rounded-lg hover:bg-gray-100 transition-all shadow-md"
            >
              Login
            </button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-3 sm:px-4 py-4 sm:py-8">
        {/* Header */}
        <div className="bg-white bg-opacity-10 backdrop-blur-md rounded-xl shadow-xl p-4 sm:p-6 mb-4 sm:mb-6 border border-white border-opacity-20">
          <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">🔄 Return Request Details</h1>
          <p className="text-sm sm:text-base text-orange-100">
            Return ID: <span className="font-bold">#{returnData.return_id}</span>
          </p>
        </div>

        {/* Status Banner */}
        <div className={`p-4 rounded-xl mb-6 border-2 ${getStatusBadgeColor(returnData.return_status)}`}>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <span className="text-3xl">{getStatusIcon(returnData.return_status)}</span>
              <div>
                <p className="font-bold text-lg uppercase">{returnData.return_status}</p>
                <p className="text-xs mt-1">
                  {returnData.return_status?.toLowerCase() === 'open' && 'Awaiting payment processing'}
                  {returnData.return_status?.toLowerCase() === 'close' && 'Return request completed'}
                  {returnData.return_status?.toLowerCase() === 'pending' && 'Return request pending approval'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Return Information */}
        <div className="bg-white rounded-2xl shadow-xl border-2 border-gray-200 overflow-hidden mb-6">
          <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 px-4 sm:px-6 py-4">
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              Return Information
            </h3>
          </div>

          <div className="p-4 sm:p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-gradient-to-br from-red-50 to-rose-50 rounded-xl p-4 border-2 border-red-100">
                <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Return ID</p>
                <p className="font-bold text-red-600 font-mono text-lg">#{returnData.return_id}</p>
              </div>
              <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-4 border-2 border-orange-100">
                <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Shipment ID</p>
                <p className="font-bold text-orange-600 font-mono text-lg">#{returnData.shipment_id}</p>
              </div>
              <div className="bg-gray-50 rounded-xl p-4 border-2 border-gray-200">
                <p className="text-xs font-semibold text-gray-500 uppercase mb-1">{t('common.createdBy')}</p>
                <p className="text-gray-900 font-semibold">{returnData.created_by || 'N/A'}</p>
              </div>
              <div className="bg-gray-50 rounded-xl p-4 border-2 border-gray-200">
                <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Created Date</p>
                <p className="text-gray-900">
                  {returnData.created_at
                    ? new Date(returnData.created_at).toLocaleString('en-IN', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })
                    : 'N/A'}
                </p>
              </div>
              <div className="bg-gray-50 rounded-xl p-4 border-2 border-gray-200">
                <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Return Status</p>
                <span
                  className={`inline-block px-3 py-1 rounded-full text-xs font-semibold uppercase ${getStatusBadgeColor(returnData.return_status)}`}
                >
                  {returnData.return_status}
                </span>
              </div>
              <div className="bg-gray-50 rounded-xl p-4 border-2 border-gray-200">
                <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Last Updated</p>
                <p className="text-gray-900">
                  {returnData.updated_at
                    ? new Date(returnData.updated_at).toLocaleString('en-IN', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })
                    : 'N/A'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Reason for Return */}
        <div className="bg-white rounded-2xl shadow-xl border-2 border-gray-200 overflow-hidden mb-6">
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-4 sm:px-6 py-4">
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              Reason for Return
            </h3>
          </div>
          <div className="p-4 sm:p-6">
            <div className="bg-blue-50 border-2 border-blue-200 p-4 rounded-xl">
              <p className="text-gray-900 whitespace-pre-wrap">{returnData.reason_for_return}</p>
            </div>
          </div>
        </div>

        {/* Payment Timeline */}
        <div className="bg-white rounded-2xl shadow-xl border-2 border-gray-200 overflow-hidden">
          <div className="bg-gradient-to-r from-purple-500 to-purple-600 px-4 sm:px-6 py-4 flex flex-wrap items-center justify-between gap-3">
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              Payment Timeline
            </h3>
            {returnData.has_payment_receipt && (
              <button
                onClick={() => setShowReceiptModal(true)}
                className="px-4 sm:px-6 py-2 sm:py-2.5 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-lg font-semibold transition flex items-center gap-2 text-sm sm:text-base"
              >
                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                  />
                </svg>
                View Receipt
              </button>
            )}
          </div>
          <div className="p-4 sm:p-6 space-y-6">
            {/* Payment Initiated */}
            <div className="flex gap-4">
              <div className="flex flex-col items-center">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-white shadow-md ${
                    isPaymentInitiated ? 'bg-green-500' : 'bg-gray-300'
                  }`}
                >
                  1
                </div>
                <div className={`w-1 h-12 my-1 ${isPaymentRefunded ? 'bg-green-500' : 'bg-gray-300'}`}></div>
              </div>
              <div className="flex-1">
                <p className="font-bold text-gray-900 text-lg">Payment Initiated</p>
                {isPaymentInitiated ? (
                  <div className="text-sm text-gray-600 mt-2 bg-green-50 p-3 rounded-lg border border-green-200">
                    <p className="mb-1">
                      <span className="font-semibold">By:</span> {returnData.payment_initiated?.initiated_by?.user_name}
                    </p>
                    <p>
                      <span className="font-semibold">Date:</span>{' '}
                      {returnData.payment_initiated?.timestamp
                        ? new Date(returnData.payment_initiated.timestamp).toLocaleString('en-IN', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })
                        : 'N/A'}
                    </p>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 mt-2 bg-gray-50 p-3 rounded-lg border border-gray-200">
                    Pending - Payment not yet initiated
                  </p>
                )}
              </div>
            </div>

            {/* Payment Refunded */}
            <div className="flex gap-4">
              <div className="flex flex-col items-center">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-white shadow-md ${
                    isPaymentRefunded ? 'bg-green-500' : 'bg-gray-300'
                  }`}
                >
                  2
                </div>
              </div>
              <div className="flex-1">
                <p className="font-bold text-gray-900 text-lg">Payment Refunded</p>
                {isPaymentRefunded ? (
                  <div className="text-sm text-gray-600 mt-2 bg-green-50 p-3 rounded-lg border border-green-200">
                    <p className="mb-1">
                      <span className="font-semibold">By:</span> {returnData.payment_refunded?.initiated_by?.user_name}
                    </p>
                    <p>
                      <span className="font-semibold">Date:</span>{' '}
                      {returnData.payment_refunded?.timestamp
                        ? new Date(returnData.payment_refunded.timestamp).toLocaleString('en-IN', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })
                        : 'N/A'}
                    </p>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 mt-2 bg-gray-50 p-3 rounded-lg border border-gray-200">
                    Pending - Payment not yet refunded
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-white bg-opacity-10 backdrop-blur-md border-t border-white border-opacity-20 text-center py-4 sm:py-6 mt-8 sm:mt-16">
        <p className="text-white text-sm sm:text-base">© 2025 Logistics Manager. All rights reserved.</p>
      </footer>

      <ReceiptModal />
    </div>
  );
};

export default PublicViewReturn;